import { Error as BaseError, Input as BaseInput, HelpText, Label, RequiredStar } from "../common";
import { corners, marginTop, marginX, media } from "../../../styles";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { InputElement, SharedInputProps } from "../Input";
import React, { useState } from "react";
import styled, { css } from "styled-components";
import { applyOpacity } from "../../../styles/utils";
import { Button as BaseButton } from "../../Button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
export type NumberProps = SharedInputProps & {
  // TODO: When we update to react>=18, remove this required prop and use the useId hook instead
  id: string;
  onValueChange: (newValue?: number) => void;
};
const Button = styled(BaseButton).attrs({
  variant: "tertiary"
})`
  min-width: 2.25rem;
  border: 0;
  background: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.05)};
  ${({
  "aria-disabled": ariaDisabled
}) => ariaDisabled ? css`
          cursor: not-allowed !important;
          opacity: 50%;
        ` : null}

  @media ${media.hoverable} {
    &:hover {
      background: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.1)};
    }
  }
`;
const Input = styled(BaseInput)`
  // Remove native increment/decrement buttons
  appearance: textfield;
  -moz-appearance: textfield;
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  width: 3rem;
  ${corners.m}
  text-align: center;
  ${marginX.s}
`;
const Error = styled(BaseError)`
  ${marginTop.xs}
`;
export function Number({
  error,
  helpText,
  label,
  onValueChange,
  ...props
}: NumberProps) {
  const {
    id,
    required,
    disabled
  } = props;
  const max = props.max !== undefined ? parseInt(props.max.toString()) : undefined;
  const min = props.min !== undefined ? parseInt(props.min.toString()) : 1;
  const startValue = props.value !== undefined ? parseInt(props.value.toString()) : undefined;
  const {
    t
  } = useTranslation();
  const [value, setValue] = useState<number | undefined>(required && !startValue ? 1 : startValue);
  const updateValue = (newValue?: number) => {
    setValue(newValue);
    onValueChange(newValue);
  };
  const onChange = (event: React.ChangeEvent<InputElement>) => {
    const {
      value
    } = event.target;
    if (value === "") return updateValue();
    const parsedValue = parseInt(value.replace(/\D/, ""));
    const isBelowMin = parsedValue < min;
    const isAboveMax = max !== undefined && parsedValue > max;
    if (isBelowMin) return updateValue(min);
    if (isAboveMax) return updateValue(max);
    updateValue(parsedValue);
  };
  const canIncrement = max !== 0 && (!max || !value || value + 1 <= max);
  const increment = () => {
    const newValue = value !== undefined ? value + 1 : min;
    if (canIncrement && !disabled) updateValue(newValue);
  };
  const canDecrement = !value || value - 1 >= min;
  const decrement = () => {
    const newValue = value ? value - 1 : min;
    if (canDecrement && !disabled) updateValue(newValue);
  };
  const hasError = !!error;
  return <>
      <Label htmlFor={id} data-sentry-element="Label" data-sentry-source-file="Number.tsx">
        {label}
        {label && required ? <RequiredStar /> : null}
      </Label>
      <HelpText data-sentry-element="HelpText" data-sentry-source-file="Number.tsx">{helpText}</HelpText>
      <Container data-sentry-element="Container" data-sentry-source-file="Number.tsx">
        <Button aria-label={t("decrementButtonLabel", {
        label: label ?? props["aria-label"]
      })} aria-disabled={!canDecrement || disabled} onClick={decrement} data-sentry-element="Button" data-sentry-source-file="Number.tsx">
          <FontAwesomeIcon icon={faMinus} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="Number.tsx" />
        </Button>
        <Input {...props} type="number" error={hasError} max={max} min={min} disabled={disabled} aria-required={required} onChange={onChange} value={value !== undefined ? value : ""} pattern="[0-9]*" inputMode="numeric" data-sentry-element="Input" data-sentry-source-file="Number.tsx" />
        <Button aria-label={t("incrementButtonLabel", {
        label: label ?? props["aria-label"]
      })} aria-disabled={!canIncrement || disabled} onClick={increment} data-sentry-element="Button" data-sentry-source-file="Number.tsx">
          <FontAwesomeIcon icon={faPlus} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="Number.tsx" />
        </Button>
      </Container>
      {error ? <Error>{error}</Error> : null}
    </>;
}
const Container = styled.div`
  display: flex;
  flex-direction: row;
`;
export default Number;